import React from 'react';
import Swal from 'sweetalert2';

const QuotasError = ({ onGenerateQuotas, period, year }) => {
  const [ufValue, setUfValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ufValue.trim()) return;
    const confirmation = await Swal.fire({
      title: 'Confirmar',
      text: `¿Estás seguro de que el valor de la UF es $${ufValue}? Luego de confirmar, se generarán las cuotas sociales para el periodo de ${period} ${year}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, generar cuotas',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#159544] focus:ring-[#1BBB55]',
        cancelButton: 'bg-[#DC3465] text-white hover:bg-[#C62839] focus:ring-[#DC3465]',
      },
    });
    if (!confirmation.isConfirmed) return;
    setIsLoading(true);
    // Simular llamada al backend
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Llamar función del padre para generar cuotas
    onGenerateQuotas(parseFloat(ufValue));
    setIsLoading(false);
  };

  return (
    <div className="bg-white border border-[#DC3465]/20 rounded-lg overflow-hidden">
      {/* Header con mensaje de error */}
      <div className="bg-gradient-to-r from-[#DC3465]/10 to-[#DC3465]/5 border-b border-[#DC3465]/20 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-[#DC3465]/10 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-[#DC3465]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#DC3465] mb-2">
              No se pudieron generar las cuotas sociales
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Ocurrió un error al intentar generar las cuotas sociales para el mes de <strong>{period} {year}</strong>. 
              Para continuar, necesitas ingresar manualmente el valor de la UF correspondiente al día 5 de este mes.
            </p>
          </div>
        </div>
      </div>

      {/* Formulario para ingresar UF */}
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campo UF */}
          <div>
            <label htmlFor="uf-value" className="block text-sm font-medium text-gray-700 mb-2">
              Valor UF del día 5 de {period} {year}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                $
              </span>
              <input
                id="uf-value"
                type="text"
                inputMode="numeric" // para mostrar teclado numérico en móviles
                pattern="\d*"
                value={ufValue}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^\d]/g, ''); // solo dígitos
                  setUfValue(numericValue);
                }}
                onKeyDown={(e) => {
                  // Prevenir punto, coma y letras
                  if (e.key === '.' || e.key === ',' || e.key === 'e') {
                    e.preventDefault();
                  }
                }}
                placeholder="Ej: 37821"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] transition-colors"
                required
                disabled={isLoading}
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Ingresa el valor en pesos chilenos (sin puntos ni comas)
            </p>
          </div>

          {/* Enlaces de referencia */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-3">
              <strong>¿Dónde consultar el valor de la UF?</strong>
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.sii.cl/valores_y_fechas/uf/uf2025.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-[#0488AF] hover:text-[#09274A] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="text-sm font-medium">SII</span>
              </a>
              <a
                href="https://si3.bcentral.cl/indicadoressiete/secure/Serie.aspx?gcode=UF&param=RABmAFYAWQB3AGYAaQBuAEkALQAzADUAbgBNAGgAaAAkADUAVwBQAC4AbQBYADAARwBOAGUAYwBjACMAQQBaAHAARgBhAGcAUABTAGUAYwBsAEMAMQA0AE0AawBLAF8AdQBDACQASABzAG0AXwA2AHQAawBvAFcAZwBKAEwAegBzAF8AbgBMAHIAYgBDAC4ARQA3AFUAVwB4AFIAWQBhAEEAOABkAHkAZwAxAEEARAA="
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-[#0488AF] hover:text-[#09274A] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="text-sm font-medium">Banco Central</span>
              </a>
            </div>
          </div>

          {/* Botón de envío */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!ufValue.trim() || isLoading}
              className={`px-6 py-3 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 
                ${!ufValue.trim() || isLoading ? 'bg-gray-700' : 'bg-[#1BBB55] hover:bg-[#159544] focus:ring-[#1BBB55]'}`}
                          >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generando cuotas...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Generar Cuotas</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuotasError;